import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Autocomplete } from '@material-ui/lab';

import { useStyles } from './PickerSelect.styles';
import { PickerSelectOption, PickerSelectProps } from './PickerSelect.types';

export const PickerSelect: FC<PickerSelectProps> = ({
  title,
  description,
  required,
  value,
  onChange,
  loading = false,
  loadingMessage = 'Loading...',
  errorText,
  options,
  labelId,
  onRefresh,
  refreshing = false,
  enableSearch = false,
}) => {
  const allErrors = errorText ? [errorText] : [];
  const hasErrors = allErrors.length > 0;
  const classes = useStyles();

  // Sort options alphabetically by label (case-insensitive)
  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      ),
    [options],
  );

  // Find selected option for Autocomplete
  // Ensure selected option exists in current options list to avoid validation errors
  const selectedOption = useMemo(() => {
    // Treat empty string as no selection
    if (!value || value === '') return null;
    const found = sortedOptions.find(opt => opt.value === value);
    // Only return if found in current options list
    return found || null;
  }, [sortedOptions, value]);

  const isDisabled = loading || refreshing;
  const displayLoadingMessage = refreshing
    ? 'Refreshing...'
    : loadingMessage;

  // Manage input value to show loading message when refreshing
  // Always control inputValue to avoid controlled/uncontrolled state switch
  // Initialize with selected option label if available, otherwise empty string
  const [inputValue, setInputValue] = useState<string>(selectedOption?.label || '');
  const isUserTypingRef = useRef<boolean>(false);
  
  // Clear value if selected option is no longer in options list (e.g., filtered out)
  useEffect(() => {
    if (!refreshing && !loading && value && !selectedOption) {
      // Selected value is no longer in options (filtered out), clear it
      onChange('');
    }
  }, [value, selectedOption, refreshing, loading, onChange]);

  useEffect(() => {
    if (refreshing) {
      // Show loading message in input when refreshing
      setInputValue(displayLoadingMessage);
      isUserTypingRef.current = false;
    } else if (!isUserTypingRef.current) {
      // When not refreshing and user not typing, sync with selected option
      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else {
        setInputValue('');
      }
    }
  }, [refreshing, displayLoadingMessage, selectedOption]);

  // Compute helper text based on state
  const getHelperText = () => {
    if (hasErrors) return allErrors[0];
    if (loading || refreshing) return displayLoadingMessage;
    return description;
  };

  // Render refresh button if onRefresh is provided
  const refreshButton = onRefresh && (
    <Tooltip title="Refresh projects">
      <span>
        <IconButton
          onClick={onRefresh}
          disabled={isDisabled}
          size="small"
          aria-label="Refresh projects"
        >
          {loading || refreshing ? (
            <CircularProgress size={20} />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  // Render with Autocomplete (search enabled)
  if (enableSearch) {
    return (
      <Box>
        <Autocomplete
          options={sortedOptions}
          getOptionLabel={option => option.label}
          getOptionSelected={(option: PickerSelectOption, selectedValue: PickerSelectOption) => option.value === selectedValue.value}
          filterOptions={(filteredOptions, state) => {
            // Default filter - filter by input value
            const input = state.inputValue.toLowerCase();
            if (!input) return filteredOptions;
            return filteredOptions.filter(option =>
              option.label.toLowerCase().includes(input)
            );
          }}
          value={selectedOption || null}
          onChange={(_event, newValue) => {
            onChange(newValue ? newValue.value : '');
            // Reset typing flag when option selected
            isUserTypingRef.current = false;
          }}
          disabled={isDisabled}
          inputValue={inputValue}
          autoSelect={false}
          autoHighlight={false}
          clearOnBlur={false}
          selectOnFocus={false}
          onInputChange={(_event, newInputValue, reason) => {
            // Prevent input changes during refresh
            if (refreshing) return;
            
            // Track user typing
            if (reason === 'input') {
              isUserTypingRef.current = true;
            } else if (reason === 'reset' || reason === 'clear') {
              isUserTypingRef.current = false;
            }
            
            // Allow user to type/search
            setInputValue(newInputValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={title}
              required={required}
              error={hasErrors}
              margin="normal"
              variant="outlined"
              placeholder={
                loading || refreshing ? displayLoadingMessage : 'Search projects...'
              }
              helperText={getHelperText()}
              inputProps={{
                ...params.inputProps,
                'aria-label': title,
                'aria-required': required,
                'aria-invalid': hasErrors,
                'aria-describedby': `${labelId}-helper-text`,
              }}
              InputProps={{
                ...params.InputProps,
                className: params.InputProps?.className 
                  ? `${params.InputProps.className} ${classes.autocompleteInputRoot}`
                  : classes.autocompleteInputRoot,
                endAdornment: (
                  <>
                    {refreshButton}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={option => (
            <Box>
              <Typography variant="body2">{option.label}</Typography>
              {option.description && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {option.description}
                </Typography>
              )}
            </Box>
          )}
          noOptionsText={
            loading || refreshing ? displayLoadingMessage : 'No projects found'
          }
          loadingText={displayLoadingMessage}
          aria-label={title}
        />
      </Box>
    );
  }

  // Render with Select (default, no search)
  return (
    <Box>
      <FormControl
        fullWidth
        margin="normal"
        error={hasErrors}
        variant="outlined"
      >
        <InputLabel id={labelId} required={required}>
          {title}
        </InputLabel>
        <Select
          labelId={labelId}
          label={title}
          value={value}
          onChange={e => onChange(e.target.value as string)}
          required={required}
          variant="outlined"
          disabled={isDisabled}
          classes={{ select: classes.select }}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { style: { maxHeight: 300 } },
          }}
          endAdornment={
            onRefresh && <InputAdornment position="end">{refreshButton}</InputAdornment>
          }
          inputProps={{
            'aria-label': title,
            'aria-required': required,
            'aria-invalid': hasErrors,
            'aria-describedby': `${labelId}-helper-text`,
          }}
        >
          {isDisabled && (
            <MenuItem value="" disabled>
              <Typography variant="body2" color="textSecondary">
                {displayLoadingMessage}
              </Typography>
            </MenuItem>
          )}
          {!isDisabled &&
            sortedOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box>
                  <Typography variant="body2">{opt.label}</Typography>
                  {opt.description && (
                    <Typography variant="caption" color="textSecondary">
                      {opt.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
        </Select>

        {hasErrors && (
          <FormHelperText error id={`${labelId}-helper-text`}>
            {allErrors[0]}
          </FormHelperText>
        )}

        {!hasErrors && (loading || refreshing) && (
          <FormHelperText id={`${labelId}-helper-text`}>
            {displayLoadingMessage}
          </FormHelperText>
        )}

        {!hasErrors && !(loading || refreshing) && description && (
          <FormHelperText id={`${labelId}-helper-text`}>
            {description}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
